import { CSSProperties, ReactNode, memo, useMemo } from 'react';
import { css } from '@emotion/react';
import { breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { NakedButton, Text, TextSize, Truncated } from '@frontend/design-system';
import { useChartContext } from '../chart.provider';
import { defaultColor } from '../helpers';
import { FormatValue } from '../types';

export type CustomLegendData = {
  color?: string;
  label?: string | ReactNode;
  value?: number | string;
};

type LegendProps = {
  customData?: CustomLegendData;
  formatValue?: FormatValue;
  isHovered?: boolean | null;
  labelTransform?: CSSProperties['textTransform'];
  legendId: string;
  legendValue?: number | string;
  onClick?: ((legendId: string) => void) | null;
  size: TextSize;
  truncateLabels?: boolean;
};

export const Legend = memo(
  ({
    customData = {},
    formatValue,
    isHovered,
    labelTransform,
    legendId,
    legendValue,
    onClick,
    size,
    truncateLabels,
  }: LegendProps) => {
    const { activeLegends, colors = {}, labels = {} } = useChartContext();

    const { color, label, showFadeEffect, value } = useMemo(() => {
      return {
        label: customData.label ?? labels[legendId] ?? legendId,
        color: customData.color ?? colors[legendId] ?? defaultColor,
        value: customData.value ?? legendValue,
        showFadeEffect: isHovered === null ? false : !isHovered,
      };
    }, [colors, customData, labels, legendId, legendValue, isHovered]);

    // Value can be 0 so we check for undefined or null only
    const hasValue = value !== undefined && value !== null;

    return (
      <NakedButton
        className={`${activeLegends.includes(legendId) ? 'active' : 'hidden'} ${onClick ? 'clickable' : 'read-only'} ${
          truncateLabels ? 'truncate-button' : ''
        }`.trim()}
        css={styles.button}
        onClick={(e) => {
          e.stopPropagation();
          onClick?.(legendId);
        }}
        style={{
          opacity: showFadeEffect ? 0.5 : 1,
        }}
        trackingId={`legend-${legendId?.toLowerCase().replaceAll('_', '-')}`}
      >
        <span className={`label-wrapper ${hasValue && truncateLabels ? 'truncate-value' : ''}`.trim()}>
          <span
            className='indicator small'
            style={{
              backgroundColor: color,
            }}
          />
          {typeof label === 'string' ? (
            <Truncated
              as='span'
              className='label'
              style={{
                textTransform: labelTransform,
                fontSize: theme.font.size[size],
              }}
            >
              {label}
            </Truncated>
          ) : (
            <>{label}</>
          )}
        </span>
        {hasValue && (
          <Text as='span' className='value' size={size} weight='bold' style={{ marginLeft: theme.spacing(0.5) }}>
            {formatValue && typeof value === 'number' ? formatValue(value) : value}
          </Text>
        )}
      </NakedButton>
    );
  }
);

Legend.displayName = 'Legend';

const styles = {
  button: css`
    align-items: center;
    color: ${theme.colors.neutral60};
    cursor: default;
    display: flex;
    gap: ${theme.spacing(0.5)};

    &.truncate-button {
      max-width: 100%;
      width: 100%;

      @container (min-width: ${breakpoints.medium.max}px) {
        width: auto;
      }
    }

    &.clickable {
      cursor: pointer;

      &:hover {
        color: ${theme.colors.neutral90};
      }
    }

    &.clickable.hidden {
      text-decoration: line-through;
    }

    .label-wrapper {
      align-items: center;
      display: flex;
      flex-wrap: nowrap;
      gap: ${theme.spacing(0.5)};
      max-width: 100%;

      &.truncate-value {
        max-width: calc(100% - 50px);

        @container (min-width: ${breakpoints.medium.max}px) {
          max-width: 100%;
        }
      }
    }

    .indicator {
      display: inline-block;
      border-radius: 50%;

      &.small {
        height: ${theme.spacing(1.2)};
        min-height: ${theme.spacing(1.2)};
        min-width: ${theme.spacing(1.2)};
        width: ${theme.spacing(1.2)};
      }
    }

    .label {
      color: inherit;
    }
  `,
};
